import { useCallback } from 'react'
import { useZionAxios } from '@fs/zion-axios'
import { type Contributions } from '@fs/zion-tree-contributions'
import { type TreeId } from '@fs/zion-tree-types'
import getTreeChangeLogUrl from './getTreeChangeLogUrl'

type LastTreeChange = {
  /** True when the change log answered with an error, so the tree's last change is unknown rather than absent. */
  failed: boolean
  /** True while the change log request is in flight, so a caller can tell "not yet" from "never changed". */
  loading: boolean
  /** Re-reads the log past the cache, for a caller that knows the tree just changed. No-op without a tree id. */
  refetchLastChange: () => void
  lastChangeMs?: number
}

/**
 * Reports when a tree was last changed, taken from the tree's change log.
 *
 * The change log answers newest-first by default, so the first entry it returns is the most recent change.
 *
 * For researcher trees only — the `RESEARCHER_TREE` group type, which the UI calls a user tree and older
 * code here calls a CET. The endpoint returns no change data for family group trees. A caller that cannot
 * make the call conditionally passes `undefined` instead, which makes no request.
 *
 * A failed request reports `failed` rather than a date. The error object itself is not passed on because
 * no caller acts on which error it was — only on whether the log could answer at all.
 */
export function useLastTreeChange(treeId: TreeId | undefined | null): LastTreeChange {
  const [{ data, error, loading }, refetch] = useZionAxios<Contributions>(getTreeChangeLogUrl(treeId), {
    manual: !treeId,
  })

  /*
   * Responses are cached by URL for the life of the page, so a tree changed after its log was first read
   * keeps reporting the older date. This skips the cache and refills it, leaving every unchanged tree to
   * go on answering from it. The rejection is swallowed the way the automatic request's is: a failure is
   * already reported through `failed`, and no caller awaits this.
   */
  const refetchLastChange = useCallback(() => {
    if (treeId) refetch().catch(() => null)
  }, [refetch, treeId])

  return {
    failed: Boolean(error),
    lastChangeMs: data?.contributions?.[0]?.modificationDateMs,
    loading: Boolean(loading),
    refetchLastChange,
  }
}
